/**
 * Open-Item Status Computation
 *
 * Pure function that derives the payment status of an invoice from its
 * total amount, recorded payments, due date, and optional skonto terms.
 *
 * DISCIPLINE:
 *   - No side effects, no async, no Firestore imports.
 *   - All monetary values in integer cents (EUR).
 *   - Date comparisons use ISO 8601 string ordering (lexicographic == chronological).
 *   - Function must remain trivially unit-testable without any mocking.
 *
 * Spec reference: architecture-m1.md §2.2 (computeOpenItemStatus, gepatchte Signatur)
 *
 * @module openItemStatus
 */

/**
 * Input parameters for `computeOpenItemStatus`.
 *
 * All monetary values MUST be in integer cents.
 * All dates MUST be ISO 8601 strings (`YYYY-MM-DD`).
 */
export interface ComputeOpenItemStatusInput {
  /** Gross total of the invoice in cents.  Source: `eurosToCents(invoice.totalAmount)`. */
  storedGrandTotalCents: number;

  /**
   * Array of payment amounts in cents, one entry per `payments` document.
   * May be empty when no payments exist yet.
   */
  paymentsCents: number[];

  /**
   * Invoice issue date (BT-2, `YYYY-MM-DD`).
   * Base date for skonto window calculation — NOT the due date.
   */
  invoiceDateISO: string;

  /**
   * Date of the most recent payment (`YYYY-MM-DD`), or `null` when no payment exists.
   * Used to determine whether a skonto deduction was made within the allowed window.
   */
  paymentDateISO: string | null;

  /**
   * Invoice due date (BT-9, `YYYY-MM-DD`), or `null` when not set.
   * Only used to determine `overdue`; an invoice without a due date is NEVER `overdue`.
   */
  dueDateISO: string | null;

  /** Today's date as `YYYY-MM-DD`.  Passed in as a parameter to keep the function pure. */
  todayISO: string;

  /**
   * Skonto percentage (e.g. `2.0` for 2 %).
   * `null` means no skonto was agreed.
   * Source: `invoice.rawData.skonto.percentage`.
   */
  skontoPercentage: number | null;

  /**
   * Number of days for the skonto window (e.g. `10`).
   * `null` means no skonto was agreed.
   * Source: `invoice.rawData.skonto.days`.
   */
  skontoDays: number | null;
}

/**
 * Result of `computeOpenItemStatus`.
 */
export interface ComputeOpenItemStatusResult {
  /**
   * Payment status of the invoice:
   * - `open`           — no payment received yet
   * - `partially_paid` — partial payment received, balance still outstanding
   * - `paid`           — fully settled (or skonto deduction within allowed window)
   * - `overdue`        — open or partially paid past the due date
   */
  status: 'open' | 'partially_paid' | 'paid' | 'overdue';

  /**
   * Remaining balance in cents.
   * Zero when fully paid; negative when overpaid.
   * NEVER presented as negative in the OP UI — use `isOverpaid` flag for that.
   */
  openAmountCents: number;

  /**
   * `true` when the total received exceeds the invoice amount.
   * `paid` status is still set; this flag marks the overpayment separately.
   */
  isOverpaid: boolean;
}

// ─── Internal helpers ────────────────────────────────────────────────────────

/**
 * Adds `days` calendar days to an ISO date string and returns the result as
 * another ISO date string.  Pure, no Date-library dependency.
 *
 * @param isoDate - `YYYY-MM-DD` input date.
 * @param days    - Number of days to add (must be a non-negative integer).
 * @returns `YYYY-MM-DD` result date.
 */
export function addDaysToISO(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  // Use UTC to avoid DST shifts altering the date
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

// ─── Main function ────────────────────────────────────────────────────────────

/**
 * Derives the open-item status of an invoice from its total amount, recorded
 * payments, optional skonto terms, and optional due date.
 *
 * Rules (see architecture-m1.md §2.2 for authoritative spec):
 *
 * 1. `paidSum == 0`  →  `open`
 * 2. `openAmount <= 0`  →  `paid`  (negative openAmount means overpayment)
 * 3. `openAmount <= skontoTolerance` AND payment was within skonto window  →  `paid`
 * 4. Otherwise  →  `partially_paid`
 *
 * Overdue overlay (applied AFTER base status):
 *   if status in ('open' | 'partially_paid') AND dueDateISO < todayISO  →  `overdue`
 *   `paid` is NEVER overridden to `overdue`.
 *
 * Integer arithmetic only — no floating-point comparisons.
 *
 * @param input - see {@link ComputeOpenItemStatusInput}
 * @returns     - see {@link ComputeOpenItemStatusResult}
 */
export function computeOpenItemStatus(
  input: ComputeOpenItemStatusInput,
): ComputeOpenItemStatusResult {
  const {
    storedGrandTotalCents,
    paymentsCents,
    invoiceDateISO,
    paymentDateISO,
    dueDateISO,
    todayISO,
    skontoPercentage,
    skontoDays,
  } = input;

  // ── Derived monetary values ───────────────────────────────────────────────

  const paidSumCents = paymentsCents.reduce((acc, c) => acc + c, 0);
  const openAmountCents = storedGrandTotalCents - paidSumCents;
  const isOverpaid = openAmountCents < 0;

  // Skonto tolerance (integer, rounded, derived from gross total)
  // 0 when no skonto is agreed — tolerance of zero means every positive rest is
  // a real outstanding balance.
  const skontoToleranceCents =
    skontoPercentage == null
      ? 0
      : Math.round((storedGrandTotalCents * skontoPercentage) / 100);

  // ── Skonto window check ───────────────────────────────────────────────────

  // The skonto deadline is invoiceDate + skonto.days (BT-2 + days).
  // paymentDate must be <= deadline to qualify as a timely skonto deduction.
  const skontoDeadlineISO =
    skontoPercentage != null
      ? addDaysToISO(invoiceDateISO, skontoDays ?? 0)
      : null;

  const withinSkontoWindow =
    skontoPercentage != null &&
    paymentDateISO != null &&
    skontoDeadlineISO != null &&
    paymentDateISO <= skontoDeadlineISO;

  // ── Base status ───────────────────────────────────────────────────────────

  let status: ComputeOpenItemStatusResult['status'];

  if (paidSumCents === 0) {
    status = 'open';
  } else if (openAmountCents <= 0) {
    // Fully paid or overpaid
    status = 'paid';
  } else if (openAmountCents <= skontoToleranceCents && withinSkontoWindow) {
    // Timely skonto deduction covers the remaining balance
    status = 'paid';
  } else {
    status = 'partially_paid';
  }

  // ── Overdue overlay ───────────────────────────────────────────────────────
  // `paid` is never overridden.  An invoice without a due date is never overdue.

  if (
    (status === 'open' || status === 'partially_paid') &&
    dueDateISO != null &&
    dueDateISO < todayISO
  ) {
    status = 'overdue';
  }

  return { status, openAmountCents, isOverpaid };
}
